import { z } from "zod";
import { Id } from "./common.ts";

/**
 * Favourite spots ("Favoriten"). Owner (2026-11-04): "let users add divespots to favourites";
 * signed-in users only, private, shown as a star on the spot, a list on the account page, a
 * marked pin on the map and a "Nur Favoriten" map filter. This block is binding for API and
 * client.
 *
 * Who and where:
 * - Every signed-in, non-banned user (auth "diver": divers AND staff). Stored with the account,
 *   so the same favourites on every device. Signed out: the star is shown and leads to the
 *   login page (with a return path); nothing is kept on the device.
 * - Routes live ONLY under `/api/me/favourites`. The favourite state is never part of a public
 *   or cacheable payload: `SpotListItem`, `SpotDetail` and the export stay unchanged.
 *
 * Routes:
 * - GET `/api/me/favourites` → `Favourites` `{ spotIds }`, newest first (`created_at DESC,
 *   spot_id` as tie-break), `LIMIT FAVOURITES_MAX` in SQL. Only published, not deleted spots; a
 *   favourite whose spot is soft-deleted or not published is left out silently and keeps its
 *   row, so it comes back if the spot is restored.
 * - PUT `/api/me/favourites/:spotId` → 204. Idempotent: an existing favourite stays (its time
 *   unchanged) and answers 204 even at the cap. A spot that is unknown, deleted or not published
 *   → 404. Adding a NEW row when the user already has `FAVOURITES_MAX` rows → 409 conflict
 *   (message "favourites full"). The cap counts ALL the user's rows, hidden ones included.
 * - The PUT transaction FIRST takes a per-user lock (`pg_advisory_xact_lock` on the user id, or
 *   `SELECT … FROM "user" WHERE id = $1 FOR UPDATE`), then checks the spot, existence and the
 *   cap, then inserts (`ON CONFLICT DO NOTHING`). Without the lock two concurrent PUTs at
 *   `FAVOURITES_MAX − 1` both pass the count under READ COMMITTED and exceed the cap.
 * - DELETE `/api/me/favourites/:spotId` → 204. Idempotent (not a favourite → still 204), and it
 *   also removes a favourite whose spot is deleted or unpublished.
 * - Params: `FavouriteParam` `{ spotId }` (not `IdParam`); a malformed id → 400 bad_request with
 *   details path ["spotId"].
 * - Usual rules: signed out 401, banned 403, writes need the same-origin Origin check (in that
 *   order). Writes (PUT and DELETE) are rate-limited AFTER auth, per user id, keyed limiter
 *   "favourites": `FAVOURITES_WRITES_PER_MIN` per minute → 429 with Retry-After. GET uses no
 *   extra limiter.
 * - Every answer (GET, PUT, DELETE, errors included) is `Cache-Control: private, no-cache` with
 *   `Vary: Cookie` (as /api/me/features): mount the middleware on `/api/me/favourites` AND
 *   `/api/me/favourites/*`. The service worker never caches them (the `/api/me` rule).
 *
 * Privacy and data:
 * - Only the user sees their own favourites. No counts, no admin view, nothing in the export.
 * - NOT audited: a personal preference that changes no shared data (same as a diver's
 *   PATCH /api/me). This is an intended exception to "every write gets an audit row".
 * - Table `spot_favourites` (user_id → user ON DELETE CASCADE, spot_id → spots ON DELETE
 *   CASCADE, created_at; primary key (user_id, spot_id)). Deleting an account erases its
 *   favourites with the user row.
 *
 * Client:
 * - Star button on the spot: `aria-pressed`, label "Zu Favoriten hinzufügen" / "Aus Favoriten
 *   entfernen". Never colour-only: a filled vs outlined star, on the pin too (a star badge on
 *   favourite pins, existing tokens only). Toggles are optimistic and roll back on error.
 *   Toggles of the same spot are serialised (the next waits for the previous to settle); after
 *   the last one settles the list is refetched, so client and server cannot disagree.
 * - The star is shown only on published spots. Staff see drafts and pending spots, which have
 *   no star (PUT would 404).
 * - Wording "Favoriten"; never mixed up with "Offline speichern" (device-local, unrelated).
 * - "Nur Favoriten" map filter: shown only when signed in, the favourites query succeeded and
 *   there is at least one favourite. It TAKES EFFECT only while shown: in every other case
 *   (signed out, loading, failed, no favourites left, another account) all pins show, even if
 *   the remembered state is "on". Removing the last favourite with the filter on shows all pins
 *   again. The on/off state may be remembered per browser (localStorage), never sent anywhere.
 * - Account page "Meine Favoriten" for every role: spot name and water body (joined from the
 *   public spot list), link to the spot, remove button, an empty-state hint.
 * - Signing in or out clears the favourites query with the other private caches.
 */
export const FAVOURITES_MAX = 500;
/** PUT + DELETE per user id per minute. */
export const FAVOURITES_WRITES_PER_MIN = 60;

/** Path params of PUT/DELETE `/api/me/favourites/:spotId`. */
export const FavouriteParam = z.object({ spotId: Id }).strict();
export type FavouriteParam = z.infer<typeof FavouriteParam>;

export const Favourites = z
  .object({ spotIds: z.array(Id).max(FAVOURITES_MAX) })
  .strict()
  .refine((f) => new Set(f.spotIds).size === f.spotIds.length, "duplicate spot id");
export type Favourites = z.infer<typeof Favourites>;
